import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTemplate } from '@/contexts/TemplateContext';
import { formatCurrencyAmount } from '@/utils/currency';

export interface BookableMaterial {
  id: string;
  name: string;
  description?: string | null;
  unit?: string | null;
  base_price: number;
  is_required: boolean;
  allows_variant_selection: boolean;
  default_quantity: number;
  included_in_price: boolean;
  service_ids: string[];
  variants: Array<{
    id: string;
    name: string;
    effective_price: number;
    attributes?: Record<string, unknown>;
  }>;
}

export interface MaterialSelection {
  material_id: string;
  material_variant_id?: string | null;
  quantity: number;
}

interface MaterialsSelectionProps {
  materials: BookableMaterial[];
  selections: MaterialSelection[];
  currency?: string;
  onChange: (selections: MaterialSelection[]) => void;
}

export const MaterialsSelection: React.FC<MaterialsSelectionProps> = ({
  materials,
  selections,
  currency = 'EUR',
  onChange,
}) => {
  const { colors } = useTemplate();
  const { t } = useTranslation('booking');

  const isSelected = (materialId: string) =>
    selections.some((s) => s.material_id === materialId);

  const getSelection = (materialId: string) =>
    selections.find((s) => s.material_id === materialId);

  const toggleMaterial = (material: BookableMaterial) => {
    if (material.is_required) return;
    if (isSelected(material.id)) {
      onChange(selections.filter((s) => s.material_id !== material.id));
    } else {
      onChange([
        ...selections,
        {
          material_id: material.id,
          quantity: material.default_quantity || 1,
        },
      ]);
    }
  };

  const updateSelection = (materialId: string, patch: Partial<MaterialSelection>) => {
    onChange(
      selections.map((s) =>
        s.material_id === materialId ? { ...s, ...patch } : s
      )
    );
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold" style={{ color: colors.colors.text }}>
          {t('materials.title', { defaultValue: 'Add-ons & materials' })}
        </h2>
        <p className="mt-1" style={{ color: colors.colors.textMuted }}>
          {t('materials.subtitle', {
            defaultValue: 'Optional extras for your appointment.',
          })}
        </p>
      </div>

      <div className="space-y-4">
        {materials.map((material) => {
          const selected = isSelected(material.id) || material.is_required;
          const sel = getSelection(material.id);
          const unitCents = sel?.material_variant_id
            ? material.variants.find((v) => v.id === sel.material_variant_id)?.effective_price ??
              material.base_price
            : material.base_price;
          const priceLabel = material.included_in_price
            ? t('materials.included', { defaultValue: 'Included' })
            : formatCurrencyAmount(unitCents / 100, currency);

          return (
            <div
              key={material.id}
              className="rounded-xl border p-4"
              style={{ borderColor: colors.colors.secondary, backgroundColor: colors.colors.background }}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-medium" style={{ color: colors.colors.text }}>
                    {material.name}
                    {material.is_required && (
                      <span className="ml-2 text-xs text-red-600">
                        {t('materials.required', { defaultValue: 'Required' })}
                      </span>
                    )}
                  </div>
                  {material.description && (
                    <p className="text-sm mt-1" style={{ color: colors.colors.textMuted }}>
                      {material.description}
                    </p>
                  )}
                  <p className="text-sm mt-2 font-semibold" style={{ color: colors.colors.primary }}>
                    {priceLabel}
                  </p>
                </div>
                {!material.is_required && (
                  <button
                    type="button"
                    onClick={() => toggleMaterial(material)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                      selected ? 'bg-primary text-primary-foreground' : 'border'
                    }`}
                    style={
                      selected
                        ? { backgroundColor: colors.colors.primary, color: '#fff' }
                        : { borderColor: colors.colors.secondary, color: colors.colors.text }
                    }
                  >
                    {selected
                      ? t('materials.selected', { defaultValue: 'Selected' })
                      : t('materials.add', { defaultValue: 'Add' })}
                  </button>
                )}
              </div>

              {selected && material.allows_variant_selection && material.variants.length > 0 && (
                <div className="mt-3">
                  <label className="text-sm font-medium" style={{ color: colors.colors.text }}>
                    {t('materials.variant', { defaultValue: 'Option' })}
                  </label>
                  <select
                    className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                    value={sel?.material_variant_id ?? ''}
                    onChange={(e) => {
                      const variantId = e.target.value || null;
                      if (!sel) {
                        onChange([
                          ...selections,
                          {
                            material_id: material.id,
                            material_variant_id: variantId,
                            quantity: material.default_quantity || 1,
                          },
                        ]);
                      } else {
                        updateSelection(material.id, { material_variant_id: variantId });
                      }
                    }}
                  >
                    <option value="">{t('materials.chooseVariant', { defaultValue: 'Choose…' })}</option>
                    {material.variants.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
